// newProjectModules — the New project box's Modules list, pure (pen "Made
// for, then the modules, both optional"). An optional list of module names,
// view-ephemeral until Create:
//   - rows are guessed from "What do you want to make?" by the core rule
//     (`Core.Project.ModuleGuesses`, instant, no assistant), when the person
//     leaves the description;
//   - a row the person typed or edited is theirs, and wins: a new guess never
//     overwrites, removes or reorders it (LM-13: the modules live in one
//     place); guessed rows are replaced by the new guesses, after theirs;
//   - a guess the person removed does not come back;
//   - the list holds at most NEW_PROJECT_MODULES_MAX rows (CL-22): at the
//     ceiling no row is added and no more guesses are taken;
//   - Create takes the non-blank names, trimmed, in order.
// Complexity: O(rows + guesses) per change; both are capped at the ceiling.

export type { ModulesDraft };
export {
    addModuleRow,
    emptyModules,
    modulesFull,
    modulesToCreate,
    removeModuleRow,
    renameModuleRow,
    withGuesses,
};

import * as Core from "@bilda/core";

type ModuleRow = {
    // The row's own key (stable while the box is open).
    readonly key: string;
    readonly name: string;
    // Guessed from the description, or the person's own (typed or edited).
    readonly origin: "guessed" | "typed";
};

type ModulesDraft = {
    readonly rows: readonly ModuleRow[];
    // The description the guesses were last taken from.
    readonly guessedFrom: string;
    // Guesses the person removed (lower-cased): never offered again.
    readonly dismissed: readonly string[];
    // The next row key's ordinal.
    readonly next: number;
};

const MAX = Core.Limits.NEW_PROJECT_MODULES_MAX;

const emptyModules: ModulesDraft = {
    rows: [],
    guessedFrom: "",
    dismissed: [],
    next: 0,
};

const modulesFull = (d: ModulesDraft): boolean => d.rows.length >= MAX;

const key = (s: string) => s.trim().toLocaleLowerCase();

// The guesses from `about`, taken in: the person's rows stay as they are,
// the earlier guesses give way to the new ones (minus those already there or
// dismissed), up to the ceiling. Unchanged description: unchanged list.
const withGuesses = (d: ModulesDraft, about: string): ModulesDraft => {
    if (about === d.guessedFrom) return d;
    const theirs = d.rows.filter((r) => r.origin === "typed");
    const taken = new Set([...theirs.map((r) => key(r.name)), ...d.dismissed]);
    const guesses = Core.Project.ModuleGuesses.guessModules(about)
        .filter((g) => !taken.has(key(g)))
        .slice(0, Math.max(0, MAX - theirs.length));
    return {
        ...d,
        guessedFrom: about,
        next: d.next + guesses.length,
        rows: [
            ...theirs,
            ...guesses.map(
                (name, i): ModuleRow => ({
                    key: `module-${d.next + i}`,
                    name,
                    origin: "guessed",
                }),
            ),
        ],
    };
};

// A new empty row of the person's own at the end (none at the ceiling).
const addModuleRow = (d: ModulesDraft): ModulesDraft =>
    modulesFull(d)
        ? d
        : {
              ...d,
              next: d.next + 1,
              rows: [
                  ...d.rows,
                  { key: `module-${d.next}`, name: "", origin: "typed" },
              ],
          };

// An edit makes the row the person's own. A line break is a space.
const renameModuleRow = (
    d: ModulesDraft,
    rowKey: string,
    name: string,
): ModulesDraft => ({
    ...d,
    rows: d.rows.map((r) =>
        r.key === rowKey
            ? { ...r, name: name.replace(/[\r\n]+/g, " "), origin: "typed" }
            : r,
    ),
});

// Removing a guess dismisses it for good.
const removeModuleRow = (d: ModulesDraft, rowKey: string): ModulesDraft => {
    const row = d.rows.find((r) => r.key === rowKey);
    return row === undefined
        ? d
        : {
              ...d,
              rows: d.rows.filter((r) => r.key !== rowKey),
              dismissed:
                  row.origin === "guessed"
                      ? [...d.dismissed, key(row.name)]
                      : d.dismissed,
          };
};

// The names Create sends: non-blank, trimmed, in order, at most the ceiling.
const modulesToCreate = (d: ModulesDraft): readonly string[] =>
    d.rows
        .map((r) => r.name.trim())
        .filter((n) => n !== "")
        .slice(0, MAX);
